import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import {
  getPostventaLocations,
  updatePostventaLocations,
  getPostventaEjecutivosCitas,
  updatePostventaEjecutivosCitas,
} from '../services/postventaApi';
import { useAuth } from '../contexts/AuthContext';
import { ROLE_LABELS, ROLE_LABEL_KEYS, ROLE_COLORS, normalizeRole, getInviteRoleOptions } from '../config/permissions';
import toast from 'react-hot-toast';
import {
  UserPlusIcon,
  TrashIcon,
  PencilIcon,
  XMarkIcon,
  UserCircleIcon,
  ClockIcon,
  ClipboardDocumentIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';

// Status used when the backend answers 404 for /auth/google/connection-status
// (org Google OAuth connect flow not available): render "not connected", no console error.
const GOOGLE_CALENDAR_NOT_CONFIGURED_STATUS = Object.freeze({
  connected: false,
  configured: false,
  reason: 'org_google_oauth_connect_not_available',
});

function Team() {
  const { t } = useTranslation();
  const { tenant, can, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showInviteLinkModal, setShowInviteLinkModal] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteEmailSent, setInviteEmailSent] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'caller',
    tenant_id: '',
  });

  // Enterprise management state (super_admin only)
  const [tenants, setTenants] = useState([]);
  const [showCreateEnterpriseModal, setShowCreateEnterpriseModal] = useState(false);
  const [enterpriseFormData, setEnterpriseFormData] = useState({
    business_name: '',
    industry: '',
    analytics_layout: 'general',
  });
  const [creatingEnterprise, setCreatingEnterprise] = useState(false);
  const [googleCalendarStatus, setGoogleCalendarStatus] = useState(null);
  const [loadingGoogleCalendarStatus, setLoadingGoogleCalendarStatus] = useState(false);
  const [connectingGoogleCalendar, setConnectingGoogleCalendar] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [aboutTenant, setAboutTenant] = useState(null);
  const [aboutText, setAboutText] = useState('');
  const [savingAbout, setSavingAbout] = useState(false);
  const [savingLayout, setSavingLayout] = useState(null); // tenant_id being saved

  // Postventa locations (sucursales) management
  const [pvLocations, setPvLocations] = useState([]);
  const [newPvLocation, setNewPvLocation] = useState('');
  const [savingPvLocations, setSavingPvLocations] = useState(false);

  // Postventa ejecutivos de citas (appointment executives) management.
  // Rows: { nombre, email?, telefono?, sucursal? }. Distinct from asesores.
  const [pvEjecutivos, setPvEjecutivos] = useState([]);
  const [newPvEjecutivo, setNewPvEjecutivo] = useState({
    nombre: '',
    email: '',
    telefono: '',
    sucursal: '',
  });
  const [savingPvEjecutivos, setSavingPvEjecutivos] = useState(false);

  const isSuperAdmin = currentUser?.role === 'super_admin';
  const isFullAdmin = can('team.manage_all');
  const inviteRoleOptions = useMemo(
    () => getInviteRoleOptions(currentUser?.role),
    [currentUser?.role]
  );

  useEffect(() => {
    fetchUsers();
    fetchPendingInvites();
    // Fail-soft: endpoint may not be deployed yet on this backend.
    getPostventaLocations()
      .then((r) => setPvLocations(r?.data?.locations || []))
      .catch(() => setPvLocations([]));
    // Ejecutivos de citas catalog. Fail-soft: endpoint may not be deployed yet.
    getPostventaEjecutivosCitas()
      .then((r) => setPvEjecutivos(r?.data?.ejecutivos || []))
      .catch(() => setPvEjecutivos([]));
  }, []);

  const persistPvLocations = async (next) => {
    setSavingPvLocations(true);
    try {
      const r = await updatePostventaLocations(next);
      setPvLocations(r?.data?.locations || next);
      toast.success(t('team.locationsSaved', 'Ubicaciones guardadas'));
    } catch (e) {
      toast.error(t('team.locationsSaveFailed', 'No se pudieron guardar las ubicaciones'));
    } finally {
      setSavingPvLocations(false);
    }
  };

  const addPvLocation = () => {
    const name = newPvLocation.trim();
    if (!name) return;
    if (pvLocations.some((l) => l.toLowerCase() === name.toLowerCase())) {
      toast.error(t('team.locationExists', 'Esa ubicación ya existe'));
      return;
    }
    setNewPvLocation('');
    persistPvLocations([...pvLocations, name]);
  };

  const removePvLocation = (name) => {
    persistPvLocations(pvLocations.filter((l) => l !== name));
  };

  const persistPvEjecutivos = async (next) => {
    setSavingPvEjecutivos(true);
    try {
      const r = await updatePostventaEjecutivosCitas(next);
      setPvEjecutivos(r?.data?.ejecutivos || next);
      toast.success(t('team.ejecutivosSaved', 'Ejecutivos guardados'));
    } catch (e) {
      toast.error(
        t('team.ejecutivosSaveFailed', 'No se pudieron guardar los ejecutivos')
      );
    } finally {
      setSavingPvEjecutivos(false);
    }
  };

  const addPvEjecutivo = () => {
    const nombre = (newPvEjecutivo.nombre || '').trim();
    if (!nombre) return;
    if (pvEjecutivos.some((e) => (e.nombre || '').toLowerCase() === nombre.toLowerCase())) {
      toast.error(t('team.ejecutivoExists', 'Ese ejecutivo ya existe'));
      return;
    }
    const row = {
      nombre,
      email: (newPvEjecutivo.email || '').trim() || undefined,
      telefono: (newPvEjecutivo.telefono || '').trim() || undefined,
      sucursal: (newPvEjecutivo.sucursal || '').trim() || undefined,
    };
    setNewPvEjecutivo({ nombre: '', email: '', telefono: '', sucursal: '' });
    persistPvEjecutivos([...pvEjecutivos, row]);
  };

  const removePvEjecutivo = (nombre) => {
    persistPvEjecutivos(pvEjecutivos.filter((e) => e.nombre !== nombre));
  };

  useEffect(() => {
    fetchGoogleCalendarStatus();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const googleAuth = params.get('google_auth');
    if (!googleAuth) return;

    const mode = params.get('mode');
    const message = params.get('message');

    if (googleAuth === 'success' && mode === 'connect') {
      toast.success(t('team.googleCalendarConnected'));
      fetchGoogleCalendarStatus();
    } else {
      const reasonKey = message ? `team.googleCalendarErrorReasons.${message}` : null;
      const translatedReason = reasonKey ? t(reasonKey, { defaultValue: message }) : null;
      toast.error(translatedReason ? t('team.googleCalendarConnectFailedWithReason', { message: translatedReason }) : t('team.googleCalendarConnectFailed'));
    }

    params.delete('google_auth');
    params.delete('mode');
    params.delete('message');
    const cleanQuery = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${cleanQuery ? `?${cleanQuery}` : ''}`);
  }, [t]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchTenants();
    }
  }, [isSuperAdmin]);

  const fetchTenants = async () => {
    try {
      const response = await api.get('/tenants');
      setTenants(response.data);
    } catch (error) {
      console.error('Error fetching tenants:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/team/users');
      // Filter out pending users
      const activeUsers = response.data.filter(u => u.status !== 'pending');
      setUsers(activeUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(t('errors.failedLoadTeam'));
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingInvites = async () => {
    try {
      const response = await api.get('/team/invites');
      setPendingInvites(response.data);
    } catch (error) {
      console.error('Error fetching invites:', error);
    }
  };

  const fetchGoogleCalendarStatus = async () => {
    try {
      setLoadingGoogleCalendarStatus(true);
      const response = await api.get('/auth/google/connection-status');
      setGoogleCalendarStatus(response.data);
    } catch (error) {
      if (error?.response?.status === 404) {
        // Backend without the org Google OAuth connect flow: that IS the
        // "not connected / not configured" state, not an error worth logging.
        setGoogleCalendarStatus(GOOGLE_CALENDAR_NOT_CONFIGURED_STATUS);
      } else {
        console.error('Error fetching Google Calendar status:', error);
        setGoogleCalendarStatus(null);
      }
    } finally {
      setLoadingGoogleCalendarStatus(false);
    }
  };

  const handleConnectGoogleCalendar = async () => {
    try {
      setConnectingGoogleCalendar(true);
      const response = await api.get('/auth/google/connect', {
        params: {
          mode: 'connect',
          redirect_path: '/team',
        },
      });
      const redirectUrl = response?.data?.url;
      if (!redirectUrl) {
        throw new Error('Missing OAuth redirect URL');
      }
      window.location.href = redirectUrl;
    } catch (error) {
      console.error('Error connecting Google Calendar:', error);
      const detail = error.response?.data?.detail;
      const missing = Array.isArray(detail?.missing) ? detail.missing.join(', ') : null;
      // A 404 on /auth/google/connect means this backend has no org OAuth connect flow.
      if (detail?.code === 'oauth_not_configured' || error.response?.status === 404) {
        toast.error(missing ? t('team.googleOAuthMissingVars', { vars: missing }) : t('team.googleOAuthNotConfigured'));
      } else {
        toast.error(t('team.googleCalendarConnectFailed'));
      }
      setConnectingGoogleCalendar(false);
    }
  };

  // Users visible to the current user's role.
  // Fix (2026-10-10): managers were filtered down to role==='caller' here
  // (mirroring the old backend filter), so a tenant with admins/managers but
  // zero callers rendered "Total Members 0 / No team members" for every
  // manager. Managers now see every user of their own tenant read-only;
  // edit/delete stay caller-only via canEditUser/canDeleteUser (server 403s too).
  const visibleUsers = users;

  const visibleInvites = useMemo(() => {
    if (isFullAdmin) return pendingInvites;
    return pendingInvites.filter(i => normalizeRole(i.role) === 'caller');
  }, [pendingInvites, isFullAdmin]);

  const handleInviteUser = async (e) => {
    e.preventDefault();
    try {
      const payload = { email: formData.email, name: formData.name, role: formData.role };
      // Super admin: include tenant_id if selecting a different enterprise
      if (isSuperAdmin && formData.tenant_id) {
        payload.tenant_id = formData.tenant_id;
      }
      const response = await api.post('/team/invite', payload);
      // Use env variable for production URL, fallback to window.location.origin for local dev
      const frontendUrl = process.env.REACT_APP_FRONTEND_URL || window.location.origin;
      const fullInviteLink = `${frontendUrl}/invite/${response.data.invite_token}`;
      setInviteLink(fullInviteLink);
      setInviteEmail(formData.email);
      setInviteEmailSent(!!response.data.email_sent);
      setShowInviteModal(false);
      setShowInviteLinkModal(true);
      setFormData({ email: '', name: '', role: 'caller', tenant_id: '' });
      fetchPendingInvites();
      toast.success(t('success.invitationCreated'));
    } catch (error) {
      console.error('Error inviting user:', error);
      toast.error(error.response?.data?.detail || t('errors.failedSendInvitation'));
    }
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
    toast.success(t('success.linkCopied'));
  };

  const handleEditUser = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (savingEdit) return;

    // Guard: never silently no-op if we lost the target user id
    const userId = selectedUser?.user_id;
    if (!userId) {
      toast.error(t('errors.failedUpdateMember'));
      return;
    }

    const payload = { name: formData.name };
    // Only send role if the current user can change roles (managers can't)
    if (can('team.edit_role')) {
      payload.role = formData.role;
    }
    // Super admin can move the member to another enterprise
    if (isSuperAdmin && formData.tenant_id && formData.tenant_id !== selectedUser.tenant_id) {
      payload.tenant_id = formData.tenant_id;
    }

    setSavingEdit(true);
    try {
      await api.put(`/team/users/${userId}`, payload);
      toast.success(t('success.memberUpdated'));
      setShowEditModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.detail || t('errors.failedUpdateMember'));
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(t('team.removeConfirm'))) {
      return;
    }
    try {
      await api.delete(`/team/users/${userId}`);
      toast.success(t('success.memberRemoved'));
      fetchUsers();
      fetchPendingInvites();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || t('errors.failedRemoveMember'));
    }
  };

  const handleCreateEnterprise = async (e) => {
    e.preventDefault();
    try {
      setCreatingEnterprise(true);
      await api.post('/tenants', enterpriseFormData);
      toast.success(t('success.enterpriseCreated'));
      setShowCreateEnterpriseModal(false);
      setEnterpriseFormData({ business_name: '', industry: '' });
      fetchTenants();
    } catch (error) {
      console.error('Error creating enterprise:', error);
      toast.error(error.response?.data?.detail || t('errors.failedCreateEnterprise'));
    } finally {
      setCreatingEnterprise(false);
    }
  };

  const openAboutModal = (tenant) => {
    setAboutTenant(tenant);
    setAboutText(tenant.company_about || '');
    setShowAboutModal(true);
  };

  const handleSaveAbout = async (e) => {
    e.preventDefault();
    try {
      setSavingAbout(true);
      await api.patch(`/tenants/${aboutTenant.tenant_id}/about`, { company_about: aboutText });
      setTenants(prev => prev.map(t => t.tenant_id === aboutTenant.tenant_id ? { ...t, company_about: aboutText } : t));
      toast.success(t('team.descriptionSaved'));
      setShowAboutModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('team.descriptionSaveError'));
    } finally {
      setSavingAbout(false);
    }
  };

  const handleLayoutChange = async (tenantId, newLayout) => {
    setSavingLayout(tenantId);
    try {
      await api.patch(`/tenants/${tenantId}/about`, { company_about: tenants.find(t => t.tenant_id === tenantId)?.company_about || '', analytics_layout: newLayout });
      setTenants(prev => prev.map(t => t.tenant_id === tenantId ? { ...t, analytics_layout: newLayout } : t));
      toast.success('Vista de analytics actualizada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar vista');
    } finally {
      setSavingLayout(null);
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      role: normalizeRole(user.role),
      tenant_id: user.tenant_id || '',
    });
    setShowEditModal(true);
  };

  const getRoleBadgeColor = (role) => {
    return ROLE_COLORS[role] || ROLE_COLORS[normalizeRole(role)] || 'bg-secondary text-foreground';
  };

  const getRoleLabel = (role) => {
    const key = ROLE_LABEL_KEYS[role] || ROLE_LABEL_KEYS[normalizeRole(role)];
    if (key) return t(key);
    return ROLE_LABELS[role] || ROLE_LABELS[normalizeRole(role)] || role;
  };

  // Check if the current user can edit/delete a specific target user
  const canEditUser = (targetUser) => {
    if (isFullAdmin) return true;
    // Managers can only edit callers
    return normalizeRole(targetUser.role) === 'caller';
  };

  const canDeleteUser = (targetUser) => {
    if (isFullAdmin) return true;
    return normalizeRole(targetUser.role) === 'caller';
  };

  if (!can('team.view')) {
    return (
      <div className="text-center py-12">
        <p style={{ color: 'var(--muted)' }}>{t('team.noPermission')}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between mb-8">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-foreground sm:text-3xl sm:truncate">
            {t('team.title')}
          </h2>
          <p className="mt-1 text-sm" style={{ color: 'var(--muted)' }}>
            {isFullAdmin
              ? t('team.subtitleAdmin')
              : t('team.subtitleManager')}
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4 space-x-3">
          {isSuperAdmin && (
            <button
              onClick={() => setShowCreateEnterpriseModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              <BuildingOfficeIcon className="-ml-1 mr-2 h-5 w-5" />
              {t('team.createEnterprise')}
            </button>
          )}
          {can('team.invite') && (
            <button
              onClick={() => setShowInviteModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              <UserPlusIcon className="-ml-1 mr-2 h-5 w-5" />
              {t('team.inviteTeamMember')}
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 mb-8">
        <div className="bg-card overflow-hidden shadow rounded-xl">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserCircleIcon className="h-6 w-6" style={{ color: 'var(--muted)' }} />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium truncate" style={{ color: 'var(--muted)' }}>{t('team.totalMembers')}</dt>
                  <dd className="text-lg font-semibold text-foreground">{visibleUsers.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-card overflow-hidden shadow rounded-xl">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <EnvelopeIcon className="h-6 w-6 text-yellow-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium truncate" style={{ color: 'var(--muted)' }}>{t('team.pendingInvites')}</dt>
                  <dd className="text-lg font-semibold text-foreground">{visibleInvites.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-card overflow-hidden shadow rounded-xl">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {isSuperAdmin ? (
                  <BuildingOfficeIcon className="h-6 w-6 text-purple-500" />
                ) : (
                  <ClockIcon className="h-6 w-6" style={{ color: 'var(--muted)' }} />
                )}
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium truncate" style={{ color: 'var(--muted)' }}>
                    {isSuperAdmin ? t('team.enterprises') : t('team.organization')}
                  </dt>
                  <dd className="text-lg font-semibold text-foreground">
                    {isSuperAdmin ? tenants.length : tenant?.business_name}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Organization Google Calendar */}
      <div className="bg-card shadow rounded-xl border border-border p-5 mb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <CalendarDaysIcon className="h-5 w-5 text-primary-600" />
              <h3 className="text-base font-semibold text-foreground">{t('team.googleCalendarTitle')}</h3>
            </div>
            <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>
              {t('team.googleCalendarSubtitle')}
            </p>
            <p className="text-sm mt-3 text-foreground">
              {loadingGoogleCalendarStatus
                ? t('common.loading')
                : googleCalendarStatus?.connected
                  ? t('team.googleCalendarConnectedAs', { email: googleCalendarStatus.google_email || 'Google' })
                  : t('team.googleCalendarNotConnected')}
            </p>
            {googleCalendarStatus?.connected_by_user_email && (
              <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                {t('team.googleCalendarConnectedBy', { email: googleCalendarStatus.connected_by_user_email })}
              </p>
            )}
          </div>
          {isFullAdmin && (
            <button
              onClick={handleConnectGoogleCalendar}
              disabled={connectingGoogleCalendar}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-hover disabled:opacity-50"
            >
              {connectingGoogleCalendar ? t('team.googleCalendarConnecting') : t('team.googleCalendarConnectButton')}
            </button>
          )}
        </div>
        {!isFullAdmin && (
          <p className="text-xs mt-3" style={{ color: 'var(--muted)' }}>
            {t('team.googleCalendarAdminsOnly')}
          </p>
        )}
      </div>

      {/* Company About Section - super_admin only */}
      {isSuperAdmin && (
        <div className="bg-card shadow rounded-xl border border-border p-5 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <BuildingOfficeIcon className="h-5 w-5 text-purple-500" />
            <h3 className="text-base font-semibold text-foreground">{t('team.enterprisesHeading')}</h3>
          </div>
          <p className="text-sm mb-4" style={{ color: 'var(--muted)' }}>{t('team.enterprisesHint')}</p>
          <div className="space-y-2">
            {tenants.map(t_item => (
              <div key={t_item.tenant_id} className="flex items-center justify-between bg-background rounded-lg px-4 py-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{t_item.business_name}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>
                    {t_item.company_about ? t_item.company_about.slice(0, 80) + (t_item.company_about.length > 80 ? '…' : '') : t('team.noDescription')}
                  </p>
                </div>
                <div className="ml-4 flex items-center gap-2 flex-shrink-0">
                  <select
                    value={t_item.analytics_layout || 'general'}
                    onChange={(e) => handleLayoutChange(t_item.tenant_id, e.target.value)}
                    disabled={savingLayout === t_item.tenant_id}
                    className="text-xs border border-border rounded px-2 py-1 bg-background text-foreground cursor-pointer disabled:opacity-50"
                    title="Vista de Analytics"
                  >
                    <option value="general">General</option>
                    <option value="sales">Ventas</option>
                    <option value="cobranza">Cobranza</option>
                  </select>
                  <button
                    onClick={() => openAboutModal(t_item)}
                    className="inline-flex items-center px-3 py-1.5 border border-border rounded-full text-xs font-medium text-foreground hover:bg-secondary"
                  >
                    {t('common.edit')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending Invites Section */}
      {visibleInvites.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
          <h3 className="text-sm font-medium text-yellow-800 mb-3">{t('team.pendingInvitations')}</h3>
          <div className="space-y-2">
            {visibleInvites.map((invite) => (
              <div key={invite.user_id} className="flex items-center justify-between bg-card rounded px-3 py-2">
                <div className="flex items-center">
                  <EnvelopeIcon className="h-5 w-5 text-yellow-500 mr-2" />
                  <span className="text-sm text-foreground">{invite.email}</span>
                  <span className="ml-2 text-xs" style={{ color: 'var(--muted)' }}>({invite.name})</span>
                  <span className={`ml-2 px-1.5 py-0.5 text-xs font-medium rounded ${getRoleBadgeColor(invite.role)}`}>
                    {getRoleLabel(invite.role)}
                  </span>
                  {isSuperAdmin && invite.tenant_name && (
                    <span className="ml-2 px-1.5 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-800">
                      <BuildingOfficeIcon className="inline h-3 w-3 mr-0.5 -mt-0.5" />
                      {invite.tenant_name}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => handleDeleteUser(invite.user_id)}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  {t('common.cancel')}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Postventa Locations (sucursales) — admin-managed, feeds Postventa filter */}
      {isFullAdmin && (
        <div className="bg-card shadow sm:rounded-xl mb-8">
          <div className="px-4 py-5 sm:px-6 border-b border-border">
            <h3 className="text-lg leading-6 font-medium text-foreground">
              {t('team.postventaLocations', 'Ubicaciones de Postventa')}
            </h3>
            <p className="mt-1 text-sm" style={{ color: 'var(--muted)' }}>
              {t('team.postventaLocationsHelp', 'Sucursales disponibles en el filtro de Postventa.')}
            </p>
          </div>
          <div className="px-4 py-5 sm:px-6">
            <div className="flex flex-wrap gap-2 mb-4">
              {pvLocations.length === 0 ? (
                <span className="text-sm" style={{ color: 'var(--muted)' }}>
                  {t('team.noLocations', 'Aún no hay ubicaciones.')}
                </span>
              ) : (
                pvLocations.map((loc) => (
                  <span
                    key={loc}
                    className="inline-flex items-center gap-1.5 rounded-full bg-background border border-border px-3 py-1 text-sm text-foreground"
                  >
                    {loc}
                    <button
                      type="button"
                      onClick={() => removePvLocation(loc)}
                      disabled={savingPvLocations}
                      className="text-gray-400 hover:text-red-500 disabled:opacity-50"
                      title={t('common.delete', 'Eliminar')}
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </span>
                ))
              )}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newPvLocation}
                onChange={(e) => setNewPvLocation(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addPvLocation();
                  }
                }}
                placeholder={t('team.addLocationPlaceholder', 'Nueva ubicación (p.ej. Villahermosa)')}
                className="w-72 max-w-full border border-border rounded-md shadow-sm py-2 px-3 bg-card text-foreground focus:outline-none focus:ring-primary focus:border-primary-500 sm:text-sm"
              />
              <button
                type="button"
                onClick={addPvLocation}
                disabled={savingPvLocations || !newPvLocation.trim()}
                className="inline-flex items-center px-3 py-2 rounded-full border border-transparent shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
              >
                {t('common.add', 'Agregar')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Postventa Ejecutivos de Citas (appointment executives) — admin-managed.
          DISTINCT from the B.PRO asesor de servicio: these run the scheduling
          follow-up and are assigned per client on the Postventa cartera. */}
      {isFullAdmin && (
        <div className="bg-card shadow sm:rounded-xl mb-8" data-testid="pv-ejecutivos-citas">
          <div className="px-4 py-5 sm:px-6 border-b border-border">
            <h3 className="text-lg leading-6 font-medium text-foreground">
              {t('team.postventaEjecutivos', 'Ejecutivos de Citas (Postventa)')}
            </h3>
            <p className="mt-1 text-sm" style={{ color: 'var(--muted)' }}>
              {t(
                'team.postventaEjecutivosHelp',
                'Personas que dan seguimiento de agendado. Se asignan por cliente en la cartera de Postventa (distinto del asesor de servicio de B.PRO).'
              )}
            </p>
          </div>
          <div className="px-4 py-5 sm:px-6">
            <div className="flex flex-col gap-2 mb-4">
              {pvEjecutivos.length === 0 ? (
                <span className="text-sm" style={{ color: 'var(--muted)' }}>
                  {t('team.noEjecutivos', 'Aún no hay ejecutivos de citas.')}
                </span>
              ) : (
                pvEjecutivos.map((ej) => (
                  <div
                    key={ej.nombre}
                    className="flex items-center justify-between gap-2 rounded-lg bg-background border border-border px-3 py-2"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-foreground truncate">
                        {ej.nombre}
                        {ej.sucursal ? (
                          <span
                            className="ml-2 text-xs"
                            style={{ color: 'var(--muted)' }}
                          >
                            · {ej.sucursal}
                          </span>
                        ) : null}
                      </div>
                      {(ej.email || ej.telefono) && (
                        <div
                          className="text-xs truncate"
                          style={{ color: 'var(--muted)' }}
                        >
                          {[ej.email, ej.telefono].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removePvEjecutivo(ej.nombre)}
                      disabled={savingPvEjecutivos}
                      className="text-gray-400 hover:text-red-500 disabled:opacity-50 shrink-0"
                      title={t('common.delete', 'Eliminar')}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="text"
                value={newPvEjecutivo.nombre}
                onChange={(e) =>
                  setNewPvEjecutivo((p) => ({ ...p, nombre: e.target.value }))
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addPvEjecutivo();
                  }
                }}
                placeholder={t('team.ejecutivoNamePlaceholder', 'Nombre *')}
                className="w-48 max-w-full border border-border rounded-md shadow-sm py-2 px-3 bg-card text-foreground focus:outline-none focus:ring-primary focus:border-primary-500 sm:text-sm"
              />
              <input
                type="email"
                value={newPvEjecutivo.email}
                onChange={(e) =>
                  setNewPvEjecutivo((p) => ({ ...p, email: e.target.value }))
                }
                placeholder={t('common.email', 'Email')}
                className="w-52 max-w-full border border-border rounded-md shadow-sm py-2 px-3 bg-card text-foreground focus:outline-none focus:ring-primary focus:border-primary-500 sm:text-sm"
              />
              <input
                type="text"
                value={newPvEjecutivo.telefono}
                onChange={(e) =>
                  setNewPvEjecutivo((p) => ({ ...p, telefono: e.target.value }))
                }
                placeholder={t('team.ejecutivoPhonePlaceholder', 'Teléfono')}
                className="w-40 max-w-full border border-border rounded-md shadow-sm py-2 px-3 bg-card text-foreground focus:outline-none focus:ring-primary focus:border-primary-500 sm:text-sm"
              />
              <input
                type="text"
                value={newPvEjecutivo.sucursal}
                onChange={(e) =>
                  setNewPvEjecutivo((p) => ({ ...p, sucursal: e.target.value }))
                }
                placeholder={t('team.ejecutivoSucursalPlaceholder', 'Sucursal')}
                className="w-44 max-w-full border border-border rounded-md shadow-sm py-2 px-3 bg-card text-foreground focus:outline-none focus:ring-primary focus:border-primary-500 sm:text-sm"
              />
              <button
                type="button"
                onClick={addPvEjecutivo}
                disabled={savingPvEjecutivos || !newPvEjecutivo.nombre.trim()}
                className="inline-flex items-center px-3 py-2 rounded-full border border-transparent shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
              >
                {t('common.add', 'Agregar')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-card shadow overflow-hidden sm:rounded-xl">
        <div className="px-4 py-5 sm:px-6 border-b border-border">
          <h3 className="text-lg leading-6 font-medium text-foreground">{t('team.activeMembers')}</h3>
        </div>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : visibleUsers.length === 0 ? (
          <div className="text-center py-12">
            <UserCircleIcon className="mx-auto h-12 w-12" style={{ color: 'var(--muted)' }} />
            <h3 className="mt-2 text-sm font-medium text-foreground">{t('team.noMembers')}</h3>
            <p className="mt-1 text-sm" style={{ color: 'var(--muted)' }}>{t('team.getStartedMembers')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-background">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                  {t('common.name')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                  {t('common.email')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                  {t('common.role')}
                </th>
                {isSuperAdmin && (
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                    {t('team.enterprise')}
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                  {t('team.joined')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {visibleUsers.map((user) => (
                <tr key={user.user_id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <span className="text-primary-600 font-medium text-sm">
                            {user.name?.charAt(0)?.toUpperCase() || 'U'}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-foreground">{user.name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-foreground">{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
                      {getRoleLabel(user.role)}
                    </span>
                  </td>
                  {isSuperAdmin && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-foreground">
                        <BuildingOfficeIcon className="h-4 w-4 text-purple-500 mr-1.5" />
                        {user.tenant_name || '—'}
                      </div>
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--muted)' }}>
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {canEditUser(user) && (
                      <button
                        onClick={() => openEditModal(user)}
                        className="text-primary-600 hover:text-primary-900 mr-4"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                    )}
                    {canDeleteUser(user) && (
                      <button
                        onClick={() => handleDeleteUser(user.user_id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" style={{ backgroundColor: 'var(--overlay)' }} onClick={() => setShowInviteModal(false)} />
            <div className="inline-block align-bottom bg-card rounded-xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button onClick={() => setShowInviteModal(false)} className="hover:opacity-70" style={{ color: 'var(--muted)' }}>
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-foreground mb-4">{t('team.inviteModalTitle')}</h3>
                  <p className="text-sm mb-4" style={{ color: 'var(--muted)' }}>
                    {t('team.inviteModalDesc')}
                  </p>
                  <form onSubmit={handleInviteUser} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground">{t('common.name')}</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 bg-card text-foreground focus:outline-none focus:ring-primary focus:border-primary-500 sm:text-sm"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground">{t('common.email')}</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 bg-card text-foreground focus:outline-none focus:ring-primary focus:border-primary-500 sm:text-sm"
                        placeholder="john@company.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground">{t('common.role')}</label>
                      {inviteRoleOptions.length === 1 ? (
                        <>
                          <input
                            type="text"
                            disabled
                            value={inviteRoleOptions[0].label}
                            className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 bg-secondary text-foreground sm:text-sm"
                          />
                          <input type="hidden" value={inviteRoleOptions[0].value} />
                        </>
                      ) : (
                        <select
                          value={formData.role}
                          onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                          className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 bg-card text-foreground focus:outline-none focus:ring-primary focus:border-primary-500 sm:text-sm"
                        >
                          {inviteRoleOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      )}
                    </div>
                    {/* Enterprise selector - super_admin only */}
                    {isSuperAdmin && tenants.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-foreground">{t('team.enterprise')}</label>
                        <select
                          value={formData.tenant_id}
                          onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })}
                          className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 bg-card text-foreground focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                        >
                          <option value="">{t('team.myEnterprise')}</option>
                          {tenants.map(t_item => (
                            <option key={t_item.tenant_id} value={t_item.tenant_id}>
                              {t_item.business_name}
                            </option>
                          ))}
                        </select>
                        <p className="mt-1 text-xs" style={{ color: 'var(--muted)' }}>{t('team.enterpriseHint')}</p>
                      </div>
                    )}
                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                      <button
                        type="submit"
                        className="w-full inline-flex justify-center rounded-full border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:ml-3 sm:w-auto sm:text-sm"
                      >
                        {t('team.sendInvitation')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowInviteModal(false)}
                        className="mt-3 w-full inline-flex justify-center rounded-full border border-border shadow-sm px-4 py-2 bg-card text-base font-medium text-foreground hover:bg-background focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:w-auto sm:text-sm"
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invite Link Modal */}
      {showInviteLinkModal && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" style={{ backgroundColor: 'var(--overlay)' }} onClick={() => setShowInviteLinkModal(false)} />
            <div className="inline-block align-bottom bg-card rounded-xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button onClick={() => setShowInviteLinkModal(false)} className="hover:opacity-70" style={{ color: 'var(--muted)' }}>
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                  <EnvelopeIcon className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-lg leading-6 font-medium text-foreground mb-2">
                  {inviteEmailSent ? t('team.invitationEmailed') : t('team.invitationCreated')}
                </h3>
                <p className="text-sm mb-4" style={{ color: 'var(--muted)' }}>
                  {inviteEmailSent
                    ? <span dangerouslySetInnerHTML={{ __html: t('team.emailSentTo', { email: inviteEmail }) }} />
                    : <span dangerouslySetInnerHTML={{ __html: t('team.shareLink', { email: inviteEmail }) }} />
                  }
                </p>
                <div className="bg-secondary rounded-xl p-3 mb-4">
                  <p className="text-xs break-all font-mono" style={{ color: 'var(--muted)' }}>{inviteLink}</p>
                </div>
                <button
                  onClick={copyInviteLink}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  <ClipboardDocumentIcon className="h-5 w-5 mr-2" />
                  {t('team.copyLink')}
                </button>
                <button
                  onClick={() => setShowInviteLinkModal(false)}
                  className="ml-3 inline-flex items-center px-4 py-2 border border-border rounded-full shadow-sm text-sm font-medium text-foreground bg-card hover:bg-background focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  {t('common.done')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" style={{ backgroundColor: 'var(--overlay)' }} onClick={() => setShowEditModal(false)} />
            <div className="inline-block align-bottom bg-card rounded-xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button onClick={() => setShowEditModal(false)} className="hover:opacity-70" style={{ color: 'var(--muted)' }}>
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-foreground mb-4">{t('team.editTeamMember')}</h3>
                  <form onSubmit={handleEditUser} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground">{t('common.name')}</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 bg-card text-foreground focus:outline-none focus:ring-primary focus:border-primary-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground">{t('common.email')}</label>
                      <input
                        type="email"
                        disabled
                        value={formData.email}
                        className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 bg-secondary text-foreground sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground">{t('common.role')}</label>
                      {can('team.edit_role') ? (
                        <select
                          value={formData.role}
                          onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                          className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 bg-card text-foreground focus:outline-none focus:ring-primary focus:border-primary-500 sm:text-sm"
                        >
                          <option value="caller">{t('roles.caller')}</option>
                          <option value="manager">{t('roles.manager')}</option>
                          <option value="admin">{t('roles.admin')}</option>
                        </select>
                      ) : (
                        <input
                          type="text"
                          disabled
                          value={getRoleLabel(formData.role)}
                          className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 bg-secondary text-foreground sm:text-sm"
                        />
                      )}
                    </div>
                    {/* Enterprise selector - super_admin only */}
                    {isSuperAdmin && tenants.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-foreground">{t('team.enterprise')}</label>
                        <select
                          value={formData.tenant_id}
                          onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })}
                          className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 bg-card text-foreground focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                        >
                          {tenants.map(t_item => (
                            <option key={t_item.tenant_id} value={t_item.tenant_id}>
                              {t_item.business_name}
                            </option>
                          ))}
                        </select>
                        <p className="mt-1 text-xs" style={{ color: 'var(--muted)' }}>{t('team.enterpriseHint')}</p>
                      </div>
                    )}
                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                      <button
                        type="submit"
                        disabled={savingEdit}
                        className="w-full inline-flex justify-center rounded-full border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-60 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
                      >
                        {savingEdit ? t('common.saving', 'Guardando...') : t('team.saveChanges')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowEditModal(false)}
                        className="mt-3 w-full inline-flex justify-center rounded-full border border-border shadow-sm px-4 py-2 bg-card text-base font-medium text-foreground hover:bg-background focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:w-auto sm:text-sm"
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Enterprise Modal (super_admin only) */}
      {showCreateEnterpriseModal && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" style={{ backgroundColor: 'var(--overlay)' }} onClick={() => setShowCreateEnterpriseModal(false)} />
            <div className="inline-block align-bottom bg-card rounded-xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button onClick={() => setShowCreateEnterpriseModal(false)} className="hover:opacity-70" style={{ color: 'var(--muted)' }}>
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-foreground mb-2">{t('team.createEnterprise')}</h3>
                  <p className="text-sm mb-4" style={{ color: 'var(--muted)' }}>{t('team.createEnterpriseDesc')}</p>
                  <form onSubmit={handleCreateEnterprise} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground">{t('team.businessName')}</label>
                      <input
                        type="text"
                        required
                        value={enterpriseFormData.business_name}
                        onChange={(e) => setEnterpriseFormData({ ...enterpriseFormData, business_name: e.target.value })}
                        className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 bg-card text-foreground focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                        placeholder={t('team.businessNamePlaceholder')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground">{t('team.industry')}</label>
                      <select
                        value={enterpriseFormData.industry}
                        onChange={(e) => setEnterpriseFormData({ ...enterpriseFormData, industry: e.target.value })}
                        className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 bg-card text-foreground focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                      >
                        <option value="">{t('common.none')}</option>
                        <option value="customer_service">{t('login.customerService')}</option>
                        <option value="debt_collection">{t('login.debtCollection')}</option>
                        <option value="healthcare">{t('login.healthcare')}</option>
                        <option value="retail">{t('login.retail')}</option>
                        <option value="other">{t('login.other')}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground">{t('team.analyticsLayout')}</label>
                      <select
                        value={enterpriseFormData.analytics_layout}
                        onChange={(e) => setEnterpriseFormData({ ...enterpriseFormData, analytics_layout: e.target.value })}
                        className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 bg-card text-foreground focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                      >
                        <option value="general">{t('team.analyticsGeneral')}</option>
                        <option value="sales">{t('team.analyticsSales')}</option>
                        <option value="cobranza">{t('team.analyticsCobranza')}</option>
                      </select>
                    </div>
                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                      <button
                        type="submit"
                        disabled={creatingEnterprise}
                        className="w-full inline-flex justify-center rounded-full border border-transparent shadow-sm px-4 py-2 bg-purple-600 text-base font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                      >
                        {creatingEnterprise ? t('common.loading') : t('common.create')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowCreateEnterpriseModal(false)}
                        className="mt-3 w-full inline-flex justify-center rounded-full border border-border shadow-sm px-4 py-2 bg-card text-base font-medium text-foreground hover:bg-background focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 sm:mt-0 sm:w-auto sm:text-sm"
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Company About Edit Modal */}
      {showAboutModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl shadow-xl w-full max-w-lg">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-foreground mb-1">Descripción de la empresa</h3>
              <p className="text-sm mb-4" style={{ color: 'var(--muted)' }}>{aboutTenant?.business_name}</p>
              <form onSubmit={handleSaveAbout} className="space-y-4">
                <div>
                  <textarea
                    value={aboutText}
                    onChange={(e) => setAboutText(e.target.value.slice(0, 2000))}
                    rows={6}
                    maxLength={2000}
                    className="w-full border border-border rounded-md shadow-sm py-2 px-3 bg-background text-foreground focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm resize-none"
                    placeholder="Describe la empresa, su industria, valores, productos y cualquier contexto relevante para el agente..."
                  />
                  <p className="text-xs mt-1 text-right" style={{ color: 'var(--muted)' }}>{aboutText.length}/2000</p>
                </div>
                <div className="flex justify-end gap-3">
                  <button type="button" onClick={() => setShowAboutModal(false)} className="px-4 py-2 rounded-full border border-border text-sm font-medium text-foreground hover:bg-secondary">Cancelar</button>
                  <button type="submit" disabled={savingAbout} className="px-4 py-2 rounded-full bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-50">{savingAbout ? 'Guardando...' : 'Guardar'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Team;
